import { DestroyRef, Injectable, inject } from '@angular/core';

import { environment } from '../../../environments/environment';
import { SnackbarService } from './snackbar.service';

const VERSION_CHECK_INTERVAL_MS = 5 * 60 * 1000;

type VersionPayload = {
  version?: string;
};

@Injectable({
  providedIn: 'root'
})
export class VersionUpdateService {
  private readonly snackbar = inject(SnackbarService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly currentVersion = (environment.appVersion || '0.0.0').trim();
  private started = false;
  private reloadPromptShown = false;
  private intervalId: number | null = null;

  start(): void {
    if (this.started || typeof window === 'undefined') {
      return;
    }

    this.started = true;

    this.checkForUpdate();

    this.intervalId = window.setInterval(() => {
      this.checkForUpdate();
    }, VERSION_CHECK_INTERVAL_MS);

    const onFocus = () => this.checkForUpdate();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);

    this.destroyRef.onDestroy(() => {
      if (this.intervalId !== null) {
        window.clearInterval(this.intervalId);
      }
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    });
  }

  private async checkForUpdate(): Promise<void> {
    if (this.reloadPromptShown || typeof window === 'undefined' || document.visibilityState === 'hidden') {
      return;
    }

    try {
      const response = await fetch(`/version.json?t=${Date.now()}`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache',
        },
      });

      if (!response.ok) {
        return;
      }

      const payload = await response.json() as VersionPayload;
      const latestVersion = payload.version?.trim();

      if (!latestVersion || latestVersion === this.currentVersion) {
        return;
      }

      this.reloadPromptShown = true;
      this.snackbar.showNotification(
        'refresh',
        '',
        `A new version (${latestVersion}) is available.`,
        'Reload',
        true
      );
    } catch {
      // Ignore silent version check failures and retry on the next interval/focus.
    }
  }
}
